import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Right-anchored side drawer. Same Radix Dialog under the hood but slides
 * in from the right at a fixed width.
 */
export function Drawer({
  open,
  onOpenChange,
  header,
  footer,
  children,
  width = 640,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  header: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: number;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-navy/40 animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed right-0 top-0 z-50 h-full bg-white shadow-modal',
            'flex flex-col animate-slide-in-right',
          )}
          style={{ width }}
        >
          <Dialog.Title className="sr-only">Detail panel</Dialog.Title>
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-line">
            <div className="min-w-0 flex-1">{header}</div>
            <Dialog.Close
              className="rounded-md p-1.5 text-ink-muted hover:bg-canvas"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin">{children}</div>
          {footer && (
            <div className="border-t border-line px-6 py-3 flex items-center justify-between gap-2 bg-white">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
